import React, { forwardRef, type Ref, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Eye, EyeOff, LoaderCircle } from 'lucide-react'

import StyledInput from "@/components/ui/Input/StyledInput";

import { cn } from "@/utils/helpers/classnames";

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "onBlur" | "value"> {
  label?: string;
  required?: boolean;
  type?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  icon?: boolean;
  $width?: string;
  $marginbottom?: string;
  prefixIconStyle?: string;
  $fontSize?: string;
  $border?: string;
  $borderRadius?: string;
  height?: string;
  value?: string | number | null;
  onBlur?: (value: string) => void;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  startLabelIcon?: React.ReactNode;
  endLabelIcon?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  $bgColor?: string;
  $dataId?: string;
  $minLength?: number;
  $maxLength?: number;
  $placeholderColor?: string;
  $upperCase?: boolean;
  inputClassName?: string;
  labelClassName?: string;
  inputContainerClassName?: string;
  inputWrapperClassName?: string;
  $isLoading?: boolean;
  $disallowZero?: boolean; // Optional prop to disallow zero input
}

const Input = forwardRef(
  (
    {
      label,
      required,
      type = "text",
      onChange,
      onKeyDown,
      error,
      $fontSize,
      $marginbottom,
      $border,
      $borderRadius,
      height,
      onBlur,
      $minLength,
      $maxLength,
      value = "",
      prefixIcon,
      prefixIconStyle,
      $width,
      suffixIcon,
      startLabelIcon,
      endLabelIcon,
      disabled,
      placeholder,
      $dataId,
      $bgColor,
      $placeholderColor,
      $upperCase = false,
      labelClassName,
      inputClassName,
      inputWrapperClassName,
      inputContainerClassName,
      $isLoading,
      $disallowZero = false, // Default to false, can be set to true to disallow zero input
      ...props
    }: InputProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [inputType, setInputType] = useState(type);
    const [showPassword, setShowPassword] = useState(false);
    const [inputValue, setInputValue] = useState<string | number>(value ?? "");
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      // Update the input value if the value prop changes. this is useful when the form is reset
      setInputValue(value ?? "");
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let sanitizedValue = $upperCase
        ? DOMPurify.sanitize(e.target.value).toUpperCase()
        : DOMPurify.sanitize(e.target.value);

      if (type === "number") {
        sanitizedValue = sanitizedValue.replace(/[^0-9.]/g, "");
        const parts = sanitizedValue.split(".");
        if (parts.length > 2) {
          sanitizedValue = parts[0] + "." + parts.slice(1).join("");
        }

        // Ensure disallowZero is respected
        if ($disallowZero && Number(sanitizedValue) === 0) {
          sanitizedValue = "";
        }
      }

      if (sanitizedValue.length > Number($maxLength)) {
        return;
      }

      if (onChange) {
        onChange(sanitizedValue);
      }
      setInputValue(sanitizedValue);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedValue = e.clipboardData.getData("text");
      let sanitizedValue = $upperCase ? DOMPurify.sanitize(pastedValue).toUpperCase() : DOMPurify.sanitize(pastedValue);

      if (type === "number") {
        sanitizedValue = sanitizedValue.replace(/[^0-9.]/g, "");
        const parts = sanitizedValue.split(".");
        if (parts.length > 2) {
          sanitizedValue = parts[0] + "." + parts.slice(1).join("");
        }

        if ($disallowZero && Number(sanitizedValue) === 0) {
          sanitizedValue = "";
        }
      }

      const trimmedValue = $maxLength ? sanitizedValue.substring(0, Number($maxLength)) : sanitizedValue;

      if (onChange) {
        onChange(trimmedValue);
      }
      setInputValue(trimmedValue);
    };

    const handleVisibility = () => {
      if (inputType === "password") {
        setShowPassword(true);
        setInputType("text");
        return;
      } else if (inputType === "text") {
        setShowPassword(false);
        setInputType("password");
        return;
      } else {
        setInputType(inputType);
        return;
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      let sanitizedValue = DOMPurify.sanitize(e.target.value);

      if (type === "number" && $disallowZero && Number(sanitizedValue) === 0) {
        sanitizedValue = "";
      }

      if (onBlur) {
        onBlur(sanitizedValue);
        if(onChange){
          onChange(sanitizedValue)
        }
      }

      setInputValue(sanitizedValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (type === "number") {
        const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "."];
        const isDigit = /[0-9]/.test(e.key);
        const isAllowed = allowedKeys.includes(e.key);
        const isModifierCombo = (e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase());

        if (!isDigit && !isAllowed && !isModifierCombo) {
          e.preventDefault();
        }

        if (e.key === "." && inputValue.toString().includes(".")) {
          e.preventDefault();
        }
      }

      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    return (
      <StyledInput
        label={label}
        ref={ref}
        $fontSize={$fontSize ?? "1rem"}
        $marginbottom={$marginbottom ?? "2rem"}
       $border={error ? "var(--error, #eb5757)" : ($border ?? "#d1d5db")}
        $borderRadius={$borderRadius ?? "0.5rem"}
        height={height ?? "2.75rem"}
        $width={$width ?? "100%"}
        required={required}
        $bgColor={$bgColor}
        $dataId={$dataId}
        $placeholderColor={$placeholderColor ?? "#A6A6A6"}
        className={props.className}
        // {...props}
      >
        {label ? (
          <>
            {startLabelIcon ? startLabelIcon : null}
            <label className={labelClassName}>{label}</label>
            {endLabelIcon ? endLabelIcon : null}
          </>
        ) : null}
        <div className={cn("inputWrapper", inputWrapperClassName)} data-disabled={disabled}>
          {$isLoading ? (
            <LoaderCircle className='absolute right-1/2 top-1/2 z-10 h-6 w-6 -translate-y-1/2 transform' />
          ) : null}
          {prefixIcon ? <div className={prefixIconStyle ?? "prefixIcon"}>{prefixIcon ? prefixIcon : null}</div> : null}
          <div className={cn("input-container", inputContainerClassName)}>
            <input
              type={inputType}
              required={required}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disabled}
              value={inputValue}
              className={cn(inputClassName)}
              onPaste={handlePaste}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              {...props}
              autoComplete='on'
              ref={inputRef}
              data-id={$dataId}
              minLength={$minLength}
              maxLength={$maxLength}
            />
            {type === "password" &&
              (showPassword ? (
                <Eye className='eye-icon' onClick={() => handleVisibility()} />
              ) : (
                <EyeOff className='eye-icon' onClick={() => handleVisibility()} />
              ))}
          </div>
          {suffixIcon ? <div className='suffixIcon'>{suffixIcon ? suffixIcon : null}</div> : null}
        </div>

        {error ? <span className='error'>{error}</span> : ""}
      </StyledInput>
    );
  }
);

Input.displayName = "Input";

export default Input;
